import React, { useState, useEffect } from "react";
import {
	DataTableExpandedRows,
	DataTableValueArray,
	DataTableRowEvent,
	DataTable,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { localeShortDateTime } from "../../../../app/datetimeUtils";
import {
	TutorialCategoryUsage,
	TutorialsByCategoryViewModel,
	UsageFilteredByWriterViewModel,
} from "./studentUsageReportApiSlice";

export interface LessonTableProps {
	className?: string;
	categoryId?: number;
	categoryUsage: UsageFilteredByWriterViewModel;
	tutorialUsage: TutorialsByCategoryViewModel[];
	onClick: (categoryId?: number) => void;
}

export function LessonTable(props: LessonTableProps) {
	const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray>();

	const data = mapLessonData(props.categoryUsage);

	useEffect(() => {
		const rows = props.categoryId ? { [props.categoryId]: true } : undefined;
		setExpandedRows(rows);
	}, [props.categoryId]);

	const onRowClick = (event: DataTableRowEvent) => {
		props.onClick(expandedRows ? undefined : event.data.categoryId);
	};

	const onRowExpand = (event: any) => {
		props.onClick(event.data.categoryId);
	};

	const onRowCollapse = (event: any) => {
		props.onClick(undefined);
	};

	const rowExpansionTemplate = () => {
		return (
			<LessonExpandedTemplate
				tutorialUsage={props.tutorialUsage}
				categoryId={props.categoryId}
			/>
		);
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column footer="Total Time" footerClassName="font-medium !text-right" colSpan={3} />
				<Column
					footer={props.categoryUsage.tutorialUsageForCourse.timeSpentString}
					footerClassName="font-medium"
				/>
			</Row>
		</ColumnGroup>
	);

	return (
		<DataTable
			value={data}
			dataKey="categoryId"
			stripedRows
			loading={props.categoryUsage?.tutorials == undefined}
			emptyMessage="No results found"
			expandedRows={expandedRows}
			onRowClick={onRowClick}
			onRowExpand={onRowExpand}
			onRowCollapse={onRowCollapse}
			rowExpansionTemplate={rowExpansionTemplate}
			selectionMode="single"
			className={`tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className ?? ""}`}
			footerColumnGroup={data && data.length > 0 && footerGroup}>
			<Column field="categoryId" hidden={true}></Column>
			<Column expander={true} className="w-8"></Column>
			<Column header="Most Recent Use" field="recentDate" className="w-2/6"></Column>
			<Column header="Category" field="categoryName" className="w-3/6"></Column>
			<Column header="Minutes" field="timeSpent" className="w-1/6"></Column>
		</DataTable>
	);
}

export interface LessonExpandedProps {
	tutorialUsage: TutorialsByCategoryViewModel[];
	categoryId?: number;
}

export function LessonExpandedTemplate(props: LessonExpandedProps) {
	const headerId = "nested-header";

	return (
		<div className="!-mx-4 !-my-3 min-w-full">
			<table className="tbl tbl-data">
				<thead>
					<tr>
						<th id={headerId} className="min-w-12"></th>
						<th id={headerId} className="w-2/6">
							First Use
						</th>
						<th id={headerId} className="w-3/6">
							Lesson Name
						</th>
						<th id={headerId} className="w-1/6">
							Minutes
						</th>
					</tr>
				</thead>
				<tbody>
					{props.tutorialUsage
						.find((d) => d.categoryId == props.categoryId)
						?.tutorials.map((row) => {
							return (
								<tr key={row.tutorialId} className="hover:!bg-inherit">
									<td></td>
									<td>{localeShortDateTime(row.dateStarted)} </td>
									<td>{row.tutorialName}</td>
									<td>{row.timeSpentString}</td>
								</tr>
							);
						})}
				</tbody>
			</table>
		</div>
	);
}

export interface LessonTableData {
	categoryId: number;
	categoryName: string;
	recentDate?: string;
	timeSpent?: string;
	row: TutorialCategoryUsage;
}

export const mapLessonData = (data: UsageFilteredByWriterViewModel): LessonTableData[] => {
	return data.tutorials.map((row) => {
		return {
			categoryId: row.tutorialCategoryId,
			recentDate: row.lastUsedDate ? localeShortDateTime(row.lastUsedDate) : "",
			categoryName: row.tutorialCategoryName,
			timeSpent: row.timeSpentString,
			row: row,
		};
	});
};
